import React, { Fragment, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Add, Cancel, Close, Delete, Done } from '@material-ui/icons';
import { Field, FieldArray, Form, Formik, getIn } from 'formik';
import * as yup from 'yup';
import { IEquipmentType } from 'pages/PageEquipmentTypePprList/interfaces';
import { SearchEquipments } from 'pages/PageEquipmentPprList/components';
import { FormikSelectRefactorComponent, FormikTextFieldComponent } from 'components/Field';
import { IEquipment, ITechnicalRoom } from '../interfaces';
import { DeleteEquipmentConfirmDialog } from '../components';

const useStyles = makeStyles({
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    zIndex: 1000,
  },
});

interface IProps {
  technicalRoom: ITechnicalRoom | null;
  disabled: boolean;
  loading: boolean;
  equipmentTypes: IEquipmentType[];
  onClose(): void;
  onSubmitTechnicalRoom(technicalRoom: ITechnicalRoom): void;
}

const TechnicalRoomDialog: React.FC<IProps & DialogProps> = ({
  open,
  technicalRoom,
  disabled,
  loading,
  equipmentTypes,
  onClose,
  onSubmitTechnicalRoom,
}) => {
  // Local state
  const [equipmentChanges, setEquipmentChanges] = useState<{
    index: number | null;
    equipment: IEquipment | null;
    options: IEquipment[];
  }>({ index: null, equipment: null, options: [] });
  const [confirmDialogOpened, setOpenedConfirmDialog] = useState(false);

  // Styles
  const classes = useStyles();

  const initialValues: ITechnicalRoom = {
    technicalRoomName: '',
    comment: '',
    modifiedEquipments: [{ equipmentType: null, equipments: [] }],
  };

  const validationSchema = yup.object().shape({
    technicalRoomName: yup.string().required('Обязательное поле'),
    modifiedEquipments: yup.array().of(
      yup.object().shape({
        equipmentType: yup.object().nullable().required('Обязательное поле'),
        equipments: yup.array().min(1, 'Обязательное поле'),
      }),
    ),
  });

  const hangleChangeEquipmentType = (
    index: number,
    equipmentType: IEquipmentType,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  ) => {
    const field = (name: string) => `modifiedEquipments[${index}].${name}`;
    setFieldValue(field('equipmentType'), equipmentType);
    setFieldValue(field('equipments'), []);
  };

  const handleDeleteEquipment = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
    const { index, options } = equipmentChanges;
    const field = (name: string) => `modifiedEquipments[${index}].${name}`;
    setFieldValue(field('equipments'), options);

    setOpenedConfirmDialog(false);
  };

  const handleOpenConfirmDialog = (index: number, options: IEquipment[], equipment: IEquipment) => {
    setEquipmentChanges({ index, options, equipment });
    setOpenedConfirmDialog(true);
  };

  const handleSubmitTechnicalRoom = (technicalRoom: ITechnicalRoom) => {
    const { modifiedEquipments, ...props } = technicalRoom;

    onSubmitTechnicalRoom({ ...props, equipments: modifiedEquipments!.map(({ equipments }) => equipments).flat() });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => reason !== 'backdropClick' && onClose(event, reason)}
      maxWidth="md"
      fullWidth
      scroll="body"
    >
      <Box
        sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, pr: 1, pl: 3 }}
      >
        <Typography variant="h6" component="h2">
          <b>{technicalRoom ? 'Обновить' : 'Добавить'} техническое помещение</b>
        </Typography>

        <Tooltip title="Закрыть">
          <IconButton aria-label="Закрыть" onClick={onClose}>
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && (
        <Box className={classes.loader}>
          <CircularProgress />
        </Box>
      )}

      <Formik
        onSubmit={handleSubmitTechnicalRoom}
        validationSchema={validationSchema}
        initialValues={technicalRoom ?? initialValues}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue }) => (
          <>
            <Form>
              <DialogContent dividers>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      name="technicalRoomName"
                      variant="filled"
                      fullWidth
                      label="Наименование"
                      disabled={disabled}
                      component={FormikTextFieldComponent}
                    />
                  </Grid>

                  <FieldArray
                    name="modifiedEquipments"
                    render={(array) => (
                      <>
                        {values.modifiedEquipments?.map((item, index) => {
                          const field = (name: string) => `modifiedEquipments[${index}].${name}`;

                          return (
                            <Fragment key={index}>
                              <Grid item lg={5} md={5} xs={12}>
                                <Field
                                  name={field('equipmentType')}
                                  label="Тип оборудования"
                                  disableClearable
                                  disabled={disabled}
                                  options={equipmentTypes ?? []}
                                  onChange={(option: IEquipmentType) =>
                                    hangleChangeEquipmentType(index, option, setFieldValue)
                                  }
                                  getOptionLabel={(option: IEquipmentType) => option.equipmentTypeName}
                                  getOptionSelected={(option: IEquipmentType, value: IEquipmentType) =>
                                    option?.equipmentTypeId === value?.equipmentTypeId
                                  }
                                  component={FormikSelectRefactorComponent}
                                />
                              </Grid>

                              <Grid item lg={6} md={6} xs={12}>
                                <SearchEquipments
                                  value={item.equipments}
                                  multiple
                                  config={{ params: { equipmentTypeId: item.equipmentType?.equipmentTypeId } }}
                                  disabled={disabled || Boolean(!item.equipmentType)}
                                  disableCloseOnSelect
                                  getOptionLabel={(option) => option.equipmentName}
                                  onChange={(event, options, reason, details) => {
                                    if (reason === 'remove-option')
                                      return handleOpenConfirmDialog(index, options, details?.option!);
                                    else setFieldValue(field('equipments'), options);
                                  }}
                                  limitTags={1}
                                  renderInput={(params) => (
                                    <TextField
                                      name={field('equipments')}
                                      label="Оборудование"
                                      variant="filled"
                                      error={Boolean(
                                        getIn(touched, field('equipments')) && getIn(errors, field('equipments')),
                                      )}
                                      helperText={
                                        Boolean(getIn(touched, field('equipments'))) &&
                                        getIn(errors, field('equipments'))
                                      }
                                      {...params}
                                    />
                                  )}
                                />
                              </Grid>

                              {Boolean(index) && (
                                <Grid item lg={1} md={1}>
                                  <Tooltip title="Удалить поля">
                                    <IconButton
                                      aria-label="Удалить поля"
                                      disabled={disabled}
                                      onClick={() => array.remove(index)}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              )}
                            </Fragment>
                          );
                        })}

                        <Grid item container justifyContent="flex-end">
                          <Tooltip title="Добавить поля">
                            <IconButton
                              color="primary"
                              disabled={disabled}
                              aria-label="Добавить поля"
                              onClick={() => array.push({ equipmentType: null, equipments: [] })}
                            >
                              <Add />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </>
                    )}
                  />

                  <Grid item xs={12}>
                    <Field
                      name="comment"
                      variant="filled"
                      fullWidth
                      multiline
                      label="Дополнительная информация"
                      disabled={disabled}
                      component={FormikTextFieldComponent}
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant="contained" disabled={disabled} onClick={onClose} startIcon={<Cancel />}>
                  Отменить
                </Button>

                <Button color="primary" variant="contained" disabled={disabled} type="submit" startIcon={<Done />}>
                  {technicalRoom ? 'Обновить' : 'Добавить'}
                </Button>
              </DialogActions>
            </Form>

            <DeleteEquipmentConfirmDialog
              open={confirmDialogOpened}
              equipment={equipmentChanges.equipment}
              disabled={disabled}
              onConfirm={() => handleDeleteEquipment(setFieldValue)}
              onClose={() => setOpenedConfirmDialog(false)}
            />
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default TechnicalRoomDialog;
